import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import { motion } from "framer-motion";
import { FaUsers } from "react-icons/fa";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

// PaginationButton — mirrors LocationsTable
const PaginationButton = ({ children, isActive, isDisabled, onClick }) => {
  return (
    <Button
      size="sm"
      variant={isActive ? "solid" : "outline"}
      colorScheme={isActive ? "orange" : "gray"}
      isDisabled={isDisabled}
      onClick={onClick}
      minW="36px"
      h="36px"
      borderRadius="md"
      _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      {children}
    </Button>
  );
};

// GlassMorphismCard wrapper — mirrors LocationsTable
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)",
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)",
  );
  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

// Tenant type badge — mirrors LocationsTable badge style
const TypeBadge = ({ type }) => {
  let color, label;
  switch (type) {
    case "cpo":
      color = "purple";
      label = "CPO";
      break;
    case "bco":
      color = "blue";
      label = "BCO";
      break;
    case "cpo_operated_by_urzza":
      color = "teal";
      label = "CPO · Urzza";
      break;
    default:
      color = "gray";
      label = type || "N/A";
  }
  return (
    <Badge
      colorScheme={color}
      variant="subtle"
      borderRadius="md"
      px={2}
      fontSize="xs"
      fontWeight="medium"
    >
      {label}
    </Badge>
  );
};

// Tenant avatar — mirrors LocationAvatar, with selected state
const TenantAvatar = ({ name, isSelected }) => {
  const initials = (name || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
  return (
    <Avatar
      name={name}
      size="sm"
      bg="#f57d36"
      color="white"
      fontWeight="bold"
      getInitials={() => initials}
      boxShadow={isSelected ? "0 0 0 2px rgba(245, 125, 54, 0.3), 0 3px 10px rgba(245, 125, 54, 0.4)" : "none"}
    />
  );
};

// Left rail: list of tenants with search and type filter.
// Defaults to payouts-enabled tenants only.
const PayoutConfigTenantsList = ({
  tenants,
  selectedTenantId,
  onSelectTenant,
  loading,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);

  const textColor = useColorModeValue("gray.700", "white");
  const subTextColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const selectedBg = useColorModeValue(
    "rgba(245, 125, 54, 0.12)",
    "rgba(245, 125, 54, 0.2)",
  );
  const selectedShadow = useColorModeValue(
    "inset 3px 0 0 #f57d36, inset 0 0 0 1px rgba(245, 125, 54, 0.2)",
    "inset 3px 0 0 #f57d36, inset 0 0 0 1px rgba(245, 125, 54, 0.3)",
  );
  const inputBg = useColorModeValue("white", "whiteAlpha.100");
  const themeColor = "#f57d36";

  const filteredTenants = useMemo(() => {
    let list = tenants.filter((t) => t.PayoutsEnabled === true);
    if (typeFilter !== "all") {
      list = list.filter((t) => t.Type === typeFilter);
    }
    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (t) =>
          t.FirstName?.toLowerCase().includes(q) ||
          t.LastName?.toLowerCase().includes(q) ||
          t.NameSpace?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [tenants, typeFilter, searchQuery]);

  // When the type filter changes, auto-select the first available tenant
  useEffect(() => {
    if (filteredTenants.length > 0) {
      onSelectTenant(filteredTenants[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter]);

  // Reset page when filters change
  useEffect(() => {
    setPageIndex(0);
  }, [typeFilter, searchQuery, tenants]);

  const pageCount = Math.max(1, Math.ceil(filteredTenants.length / pageSize));
  const currentPage = Math.min(pageIndex, pageCount - 1);
  const paginatedTenants = filteredTenants.slice(
    currentPage * pageSize,
    currentPage * pageSize + pageSize,
  );

  const tenantName = (t) =>
    `${t.FirstName || ""} ${t.LastName || ""}`.trim() || t.NameSpace || "—";

  return (
    <GlassMorphismCard
      w={{ base: "100%", md: "340px" }}
      minW={{ base: "100%", md: "320px" }}
      maxH="78vh"
      display="flex"
      flexDirection="column"
    >
      {/* Header with search — mirrors LocationsTable layout */}
      <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
        <Flex
          direction={{ base: "column", md: "column" }}
          justify="space-between"
          align="stretch"
          gap={4}
        >
          {/* Title + count on top */}
          <Flex align="center" justify="space-between">
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              Tenants
            </Text>
            <Badge
              bg={themeColor}
              color="white"
              borderRadius="full"
              px={3}
              py={1}
              fontWeight="bold"
            >
              {filteredTenants.length} total
            </Badge>
          </Flex>

          {/* Search + filter */}
          <VStack align="stretch" spacing={2.5}>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search by name, namespace..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                borderRadius="md"
                bg={inputBg}
                _focus={{
                  borderColor: themeColor,
                  boxShadow: `0 0 0 1px ${themeColor}`,
                }}
              />
            </InputGroup>
            <Select
              size="sm"
              borderRadius="md"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              bg={inputBg}
              _focus={{
                borderColor: themeColor,
                boxShadow: `0 0 0 1px ${themeColor}`,
              }}
            >
              <option value="all">All Types</option>
              <option value="cpo">CPO</option>
              <option value="bco">BCO</option>
              <option value="cpo_operated_by_urzza">CPO · Urzza</option>
            </Select>
          </VStack>
        </Flex>
      </Box>

      {/* List */}
      <Box flex={1} overflowY="auto">
        {loading ? (
          <Flex justify="center" align="center" py={12}>
            <Spinner color={themeColor} size="xl" thickness="4px" />
          </Flex>
        ) : filteredTenants.length === 0 ? (
          <MotionFlex
            direction="column"
            align="center"
            justify="center"
            py={12}
            px={4}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Icon as={FaUsers} boxSize={16} color={themeColor} mb={4} />
            <Text fontSize="xl" fontWeight="bold" mb={2}>
              No Tenants Found
            </Text>
            <Text color={subTextColor} textAlign="center" maxW="md">
              Try changing your search or type filter.
            </Text>
          </MotionFlex>
        ) : (
          <VStack spacing={0} align="stretch">
            {paginatedTenants.map((tenant) => {
              const isSelected = tenant.Id === selectedTenantId;
              return (
                <MotionBox
                  key={tenant.Id}
                  transition={{ duration: 0.2 }}
                  _hover={isSelected ? {} : { bg: hoverBg }}
                  cursor="pointer"
                  onClick={() => onSelectTenant(tenant)}
                  bg={isSelected ? selectedBg : "transparent"}
                  boxShadow={isSelected ? selectedShadow : "none"}
                  borderLeft="3px solid transparent"
                  px={4}
                  py={3}
                  _notFirst={{ borderTop: "1px solid", borderColor }}
                >
                  <HStack spacing={3} align="center">
                    {/* Column 1: Avatar */}
                    <TenantAvatar name={tenantName(tenant)} isSelected={isSelected} />
                    {/* Column 2: Name + namespace */}
                    <Box flex={1} minW={0}>
                      <Text
                        fontSize="sm"
                        color={textColor}
                        fontWeight={isSelected ? "bold" : "semibold"}
                        noOfLines={1}
                      >
                        {tenantName(tenant)}
                      </Text>
                      <Text fontSize="xs" color={subTextColor} noOfLines={1}>
                        @{tenant.NameSpace || "—"}
                      </Text>
                    </Box>
                    {/* Column 3: Type badge */}
                    <TypeBadge type={tenant.Type} />
                  </HStack>
                </MotionBox>
              );
            })}
          </VStack>
        )}
      </Box>

      {/* Pagination — mirrors LocationsTable */}
      {filteredTenants.length > 0 && (
        <Flex
          p={3}
          justify="space-between"
          align="center"
          borderTop="1px solid"
          borderColor={borderColor}
          flexWrap="wrap"
          gap={2}
        >
          <HStack>
            <Select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPageIndex(0);
              }}
              size="sm"
              borderRadius="md"
              w="110px"
              bg={inputBg}
            >
              {[5, 10, 20, 30].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Select>
            <Text fontSize="xs" color="gray.500">
              {paginatedTenants.length} of {filteredTenants.length}
            </Text>
          </HStack>

          <HStack spacing={1}>
            <PaginationButton
              isDisabled={currentPage === 0}
              onClick={() => setPageIndex(0)}
            >
              <ChevronLeftIcon boxSize={3} />
            </PaginationButton>

            {pageCount <= 5 ? (
              [...Array(pageCount)].map((_, i) => (
                <PaginationButton
                  key={i}
                  isActive={currentPage === i}
                  onClick={() => setPageIndex(i)}
                >
                  {i + 1}
                </PaginationButton>
              ))
            ) : (
              <>
                {currentPage > 1 && (
                  <PaginationButton onClick={() => setPageIndex(0)}>
                    1
                  </PaginationButton>
                )}
                {currentPage > 2 && <Text>...</Text>}
                {currentPage > 0 && (
                  <PaginationButton
                    onClick={() => setPageIndex(currentPage - 1)}
                  >
                    {currentPage}
                  </PaginationButton>
                )}
                <PaginationButton
                  isActive
                  onClick={() => setPageIndex(currentPage)}
                >
                  {currentPage + 1}
                </PaginationButton>
                {currentPage < pageCount - 1 && (
                  <PaginationButton
                    onClick={() => setPageIndex(currentPage + 1)}
                  >
                    {currentPage + 2}
                  </PaginationButton>
                )}
                {currentPage < pageCount - 3 && <Text>...</Text>}
                {currentPage < pageCount - 2 && (
                  <PaginationButton
                    onClick={() => setPageIndex(pageCount - 1)}
                  >
                    {pageCount}
                  </PaginationButton>
                )}
              </>
            )}

            <PaginationButton
              isDisabled={currentPage >= pageCount - 1}
              onClick={() => setPageIndex(currentPage + 1)}
            >
              <ChevronRightIcon boxSize={3} />
            </PaginationButton>
          </HStack>
        </Flex>
      )}
    </GlassMorphismCard>
  );
};

export default PayoutConfigTenantsList;
